import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFramePositioning } from '../../../hooks/useFramePositioning.js';
import { useOnDismiss } from '../../../hooks/useOnDismiss';
import { cx } from '../../../utils/cx';

import { formatPrice } from '../../../utils/format-number';
import { translate } from '../../../utils/wp.js';
import { LockStockDialog } from '../../dialogs/lock-stock';
import { OutOfStockDialog } from '../../dialogs/OutOfStockDialog';
import { NumberSelector } from '../../NumberSelector';

function getPriceString(selectedPackage, ticket) {
    if (ticket == null) {
        return '';
    }

    if (
        NOJESRESOR_PRODUCT.multiday &&
        ticket.stock > 0 &&
        (
            NOJESRESOR_PRODUCT.hotels.optional ||
            NOJESRESOR_PRODUCT.hotels.without
        )
    ) {
        return (
            <span color={'blue'}>
                {formatPrice(ticket.price) + ' SEK'}
            </span>
        );
    } else if (
        ticket.stock > 0 &&
        (
            NOJESRESOR_PRODUCT.hotels.optional ||
            NOJESRESOR_PRODUCT.hotels.without
        )
    ) {
        return formatPrice(ticket.price) + ' SEK';
    }

    if (
        ticket.stock < 1 ||
        ticket.price - selectedPackage.ticket.price < 0
    ) {
        return `${translate('sold_out')}!`;
    }

    let text = translate('included_in_package');

    if (ticket.price - selectedPackage.ticket.price > 0) {
        text = `+ ${formatPrice(ticket.price -
            selectedPackage.ticket.price)} SEK / person`;
    }

    if (ticket.food_ticket != null || ticket.includes_food) {
        text += ` • ${translate('food_included')}`;
    }

    return text;
}

export function TicketsSelect({
    addedTickets,
    selectedPackage,
    selectedTicket,
    tickets,
    onSelectTicket,
    onAddTicket,
    onRemoveTicket,
    onConfirmTickets,
}) {
    const [numberOfTickets, setNumberOfTickets] = useState(0);
    const [showLockStockDialog, setShowLockStockDialog] = useState(false);
    const [showLowStockDialog, setShowLowStockDialog] = useState(false);

    useEffect(() => {
        setNumberOfTickets(0);
    }, [selectedTicket]);

    if (tickets.length === 0) {
        return null;
    }

    function onChangeNumberOfTickets(value, oldValue) {
        const change = value - oldValue;

        if (change > 0) {
            onAddTicket(selectedTicket);
        } else {
            onRemoveTicket(selectedTicket);
        }

        setNumberOfTickets(value);
    }

    return (
        <>
            <LockStockDialog
                visible={showLockStockDialog}
                onClick={() => setShowLockStockDialog(false)}
                onClose={() => setShowLockStockDialog(false)}
            />
            <OutOfStockDialog
                visible={showLowStockDialog}
                onClick={() => setShowLowStockDialog(false)}
                onClose={() => setShowLowStockDialog(false)}
            />
            <Tickets
                selectedPackage={selectedPackage}
                tickets={tickets}
                addedTickets={addedTickets}
                selectedTicket={selectedTicket}
                onSelectTicket={onSelectTicket}
            />
            <div className="flex gap-x-4">
                <NumberSelector
                    value={selectedTicket != null ? numberOfTickets : 0}
                    suffix=""
                    disabled={selectedTicket == null}
                    min={0}
                    max={selectedTicket != null ? selectedTicket.stock : 0}
                    onClickDisabled={selectedTicket != null
                        ? () => setShowLowStockDialog(true)
                        : null}
                    onChange={onChangeNumberOfTickets}
                />
                <button
                    className={cx(
                        'flex-1 flex items-center justify-center gap-x-2 rounded-full px-6 py-3 text-xs font-bold text-primarytext',
                        numberOfTickets > 0 && 'bg-primary text-primarytext',
                        numberOfTickets ===
                        0 &&
                        'bg-disabled !text-black/50 cursor-default pointer-events-none',
                    )}
                    onClick={onConfirmTickets}
                >
                    <span>{translate('choose_tickets')}</span>
                </button>
            </div>
        </>
    );
}

function Tickets({
    selectedPackage,
    tickets,
    addedTickets,
    selectedTicket,
    disabled,
    onSelectTicket,
}) {
    const popup = useRef(null);

    const [isOpen, setIsOpen] = useState(false);
    const [section, setSection] = useState('tickets');

    useOnDismiss(
        popup,
        isOpen,
        useCallback(() => setIsOpen(false), [setIsOpen]),
    );

    const {center} = useFramePositioning(popup, isOpen);

    function _onSelectTicket(ticket) {
        setIsOpen(false);

        onSelectTicket(ticket);
    }

    function onOpenList() {
        if (disabled) {
            return;
        }

        setIsOpen(true);
    }

    const minPrice = Math.min(...tickets.map(ticket => ticket.price));

    return (
        <div className="mb-4">
            {selectedTicket &&
                <SelectedTicket
                    selectedPackage={selectedPackage}
                    ticket={selectedTicket}
                    onClick={onOpenList}
                    isIncluded={selectedTicket.price === minPrice}
                />
            }

            {!selectedTicket &&
                <PlaceholderTicket disabled={disabled} onClick={onOpenList} />
            }

            {isOpen &&
                <div className="fixed inset-0 z-[1] bg-black/30">
                    <div
                        className="fixed left-1/2 -translate-x-1/2 w-[850px] max-w-[90%] h-[80vh] max-h-[570px] bg-white rounded"
                        ref={popup}
                        style={{
                            top: center,
                        }}
                    >
                        <div className={'relative'}>
                            <div className="tabs flex flex-start items-start pl-3 pr-8 pt-4 border-b border-black/10 sm:pr-12 sm:pl-4">
                                <button
                                    className={cx(
                                        'pb-3 flex-1 border-b-2 border-transparent flex justify-center items-center gap-x-2.5 sm:pb-4',
                                        section === 'tickets' &&
                                        'active !border-primary',
                                    )}
                                    onClick={() => setSection('tickets')}
                                >
                                    <i className="icon-ticket hidden sm:block text-primary text-[24px]"></i>
                                    <span className="text-sm font-semibold sm:text-base">
                                        {translate('choose_ticket_category')}
                                    </span>
                                </button>

                                <button
                                    className={cx(
                                        'pb-3 flex-1 border-b-2 border-transparent flex justify-center items-center gap-x-2.5 sm:pb-4',
                                        section === 'information' &&
                                        'active !border-primary',
                                    )}
                                    onClick={() => setSection('information')}
                                >
                                    <i className="icon-info hidden sm:block text-primary text-[22px]"></i>
                                    <span className="text-sm font-semibold sm:text-base">
                                        {translate('choose_ticket_information')}
                                    </span>
                                </button>
                            </div>
                            <button
                                className="icon-plus rotate-45 text-[20px] absolute right-2 top-4 sm:top-5 sm:right-4"
                                onClick={() => setIsOpen(false)}
                            />
                        </div>
                        {section === 'tickets' &&
                            <>
                                {tickets.map(ticket => {
                                    let disabled = ticket.stock === 0 ||
                                        ticket.stock < addedTickets.length;

                                    if (
                                        addedTickets.length >
                                        0 &&
                                        ticket.category !==
                                        addedTickets[0].category &&
                                        ticket.type !==
                                        addedTickets[0].type
                                    ) {
                                        return null;
                                    }

                                    const isIncluded = ticket.price ===
                                        minPrice;

                                    return (
                                        <Ticket
                                            key={ticket.id}
                                            selectedPackage={selectedPackage}
                                            selectedTicket={selectedTicket}
                                            ticket={ticket}
                                            disabled={disabled}
                                            onClick={_onSelectTicket}
                                            isIncluded={isIncluded}
                                        />
                                    );
                                })}
                            </>
                        }

                        {section === 'information' &&
                            <div
                                className="ticket-information text-sm font-normal p-4"
                                dangerouslySetInnerHTML={{__html: NOJESRESOR_PRODUCT.information}}
                            />
                        }
                    </div>
                </div>
            }
        </div>
    );
}

function Radio({checked}) {
    return (
        <div
            className={cx(
                'min-w-4 w-4 h-4 rounded-full border-2 relative',
                checked ? 'border-primary' : 'border-black',
            )}
        >
            <span
                className={cx(
                    'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary',
                    !checked && 'hidden',
                )}
            />
        </div>
    );
}

function Ticket({
    selectedPackage,
    selectedTicket,
    ticket,
    disabled,
    onClick,
    isIncluded,
}) {
    const price = getPriceString(selectedPackage, ticket);

    const shouldShowLowStockNotice = ticket.stock > 0 &&
        ticket.stock <= woocommerce_settings.low_stock_threshold;

    const isSoldOut = ticket.stock <= 0;

    const isSelected = selectedTicket && selectedTicket.id === ticket.id;

    function _onClick() {
        if (isSoldOut || disabled || isSelected) {
            return;
        }

        onClick(ticket);
    }

    return (
        <div
            className={cx(
                'flex items-center flex-wrap gap-x-2 mx-3 mt-4 -mb-2 p-3 rounded border border-primary bg-primary/10 sm:mx-4 sm:p-4 sm:flex-nowrap',
                !disabled
                    ? 'cursor-pointer'
                    : 'bg-[#f4f4f4] cursor-not-allowed',
            )}
            onClick={_onClick}
            role="button"
            data-cy="ticket"
        >
            <Radio checked={isSelected} />
            <span className="text-sm">
                {translate(ticket.name)}
            </span>
            {shouldShowLowStockNotice &&
                <span className="opacity-75 font-normal ml-auto md:ml-0">
                    ({translate('only_x_tickets_left')
                    .replace('%d', ticket.stock)})
                </span>
            }
            <div className="basis-full h-1 sm:hidden"></div>
            <div className="ml-6 sm:ml-auto">
                <div
                    className={cx(
                        'px-1.5 rounded-sm font-semibold text-[12px] text-primarytext leading-[20px] whitespace-nowrap',
                        isIncluded && 'bg-tag',
                        isSoldOut && 'bg-red',
                        !isIncluded && !isSoldOut && 'bg-[#EBEBEB]',
                    )}
                >
                    {price}
                </div>
            </div>
        </div>
    );
}

function SelectedTicket({selectedPackage, ticket, onClick, isIncluded}) {
    const price = getPriceString(selectedPackage, ticket);

    const shouldShowLowStockNotice = ticket.stock > 0 &&
        ticket.stock <= woocommerce_settings.low_stock_threshold;

    const isSoldOut = ticket.stock <= 0;

    return (
        <div
            className="flex items-center rounded flex-wrap sm:flex-nowrap gap-x-2 p-3 bg-primary/20"
            onClick={onClick}
            role="button"
        >
            <Radio checked={true} />
            <span className="text-sm">
                {translate(ticket.name)}
            </span>
            {shouldShowLowStockNotice &&
                <span className="opacity-75 font-normal ml-auto md:ml-0">
                    ({translate('only_x_tickets_left')
                    .replace('%d', ticket.stock)})
                </span>
            }
            <div className="basis-full h-1 sm:hidden"></div>
            <div className="ml-6 sm:ml-auto">
                <div
                    className={cx(
                        'px-1.5 rounded-sm font-semibold text-[12px] text-primarytext leading-[20px] whitespace-nowrap',
                        isIncluded && 'bg-tag',
                        isSoldOut && 'bg-red',
                        !isIncluded && !isSoldOut && 'bg-[#EBEBEB]',
                    )}
                >
                    {price}
                </div>
            </div>
        </div>
    );
}

function PlaceholderTicket({disabled, onClick}) {
    return (
        <div
            className="relative border border-primary w-full rounded whitespace-nowrap"
            onClick={onClick}
        >
            <button
                className="p-3 w-full flex items-center justify-between gap-x-3"
                disabled={disabled}
            >
                <span className="text-sm">{translate('choose_ticket_category')}</span>
                <div className="icon-plus text-[16px]"></div>
            </button>
        </div>
    );
}
